// client/src/components/ProductShowcase.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ProductCard from './products/ProductCard';
import { useInView } from 'react-intersection-observer';
import './ProductShowcase.css';

// --- Configuration ---
const PRODUCTS_PER_PAGE = 8;
const CHANGE_INTERVAL = 5000; // Time each page of products is visible
const FADE_DURATION = 500;   // Duration of the fade animation

const ProductShowcase = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [isFading, setIsFading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Effect to fetch all products once
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/products`)
      .then(response => {
        setAllProducts(response.data);
        setDisplayedProducts(response.data.slice(0, PRODUCTS_PER_PAGE));
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Could not fetch products for showcase", error);
        setIsLoading(false);
      });
  }, []);

  // --- THIS IS THE FULLY IMPLEMENTED EFFECT FOR CYCLING PRODUCTS ---
  useEffect(() => {
    // Don't start the timer if there aren't enough products to cycle
    if (allProducts.length <= PRODUCTS_PER_PAGE) {
      return;
    }
    
    const pageIndex = { current: 0 }; 

    const interval = setInterval(() => {
      // 1. Trigger the fade-out animation
      setIsFading(true);

      // 2. Wait for the fade-out to finish, then change the content
      setTimeout(() => {
        const totalPages = Math.ceil(allProducts.length / PRODUCTS_PER_PAGE);
        pageIndex.current = (pageIndex.current + 1) % totalPages;
        
        const startIndex = pageIndex.current * PRODUCTS_PER_PAGE;
        const endIndex = startIndex + PRODUCTS_PER_PAGE;
        setDisplayedProducts(allProducts.slice(startIndex, endIndex));
        
        // 3. Trigger the fade-in animation
        setIsFading(false);
      }, FADE_DURATION); // Wait for the duration of the CSS fade

    }, CHANGE_INTERVAL); // This is the time between cycles

    // Cleanup function to stop the timer when the component is no longer on screen
    return () => clearInterval(interval);
  }, [allProducts]); // This effect depends on the products being loaded

  // Render function for the grid content
  const renderGrid = () => {
    if (isLoading) {
      return (
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
        </div>
      );
    }
    
    return (
      <div className={`products-showcase-grid ${isFading ? 'fading' : ''}`}>
        {displayedProducts.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    );
  };
  
  if (allProducts.length === 0 && !isLoading) {
    return null;
  }

  return (
    <section ref={ref} className={`product-showcase-section ${inView ? 'is-visible' : ''}`}>
      <div className="container">
        <h2 className="section-title">Our Full Product Range</h2>
        <p className="section-subtitle">A glimpse into our comprehensive catalog of medical solutions.</p>
        
        {renderGrid()}
      </div>
    </section>
  );
};

export default ProductShowcase;